/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import {
  ComponentItemFinderListItem,
  CostCenterFinderListItem,
  formulaValidator,
  getUomQuantityType,
} from '@gh/builder-shared';
import {
  createUomQuantity,
  Formula,
  GtoUom,
  HpeCcOrderDetailsDto,
  HpeCcOrderDetailsItemDto,
  HpeComponentDto,
  HpeComponentItemDto,
  HpeCostCenterItemRefDto,
  HpeCostCenterRefDto,
  HpeCuttingListItemDto,
  HpeDetailsDto,
  HpeDetailsInitDto,
  HpeDetailsSaveRequestComponentDto,
  HpeDetailsSaveRequestCostCenterDto,
  HpeDetailsSaveRequestDto,
  HpeDetailsSaveRequestItemDto,
  HpeDetailsSaveRequestTopDto,
  HpeGetComponentsResponseDto,
  HpeGetCostCenterRequestComponentDto,
  HpeGetCostCenterRequestCostCenterDto,
  HpeGetCostCenterRequestItemDto,
  HpeGetCostCenterRequestTopDto,
  HpeGetCostCenterResponseDto,
  HpeGetCostCenterResponseItemDto,
  HpeGetCostCenterResponseSalesTaxDto,
  HpeInitType,
  HpeTopDto,
  HpeTopRefItemDto,
  HpeUomRefItemDto,
  HpeViewMode,
  isFormulaEmpty,
  JobListItemDto,
  normalizeTakeOffParams,
  TakeOffParamDto,
  TOP_CODE_CONTRACT_PRICE,
  UomDetailsDto,
} from '@gh/builder-shared-data';
import {
  EntityConfig,
  entityStateMerger,
  entityValidatorByEntityConfig,
  errorsEntityState,
  fieldStateByEntityConfig,
  fieldValidatorByEntityConfig,
  fieldValidatorFrom,
  nestedArrayValidator,
  NumberTypes,
  setCreatedDate,
  setEntityField,
} from '@gh/common-shared';
import {
  DirtyType,
  EntityVersion,
  ID,
  WithCreatedDate,
  WithDirtyType,
  WithEntityState,
  WithUniqueId,
} from '@gh/common-shared-data';
import { createEnum, EnumSet, Quantity, WithOptimisticLock } from '@gh/core-data';
import { FormMode } from '@gh/core-ui';
import { cast, currentDate, isNotNil } from '@gh/core-util';
import { compact, flatMap, isEmpty, isNil, keyBy, map, omit, pick, uniqueId } from 'lodash';
import { isJeComponentCostCenter } from '../../../builder-job-admin/jobs/resources/job-estimate-details.model';

export const HPE_CCC_ID = 'ccc';

// tslint:disable-next-line:variable-name
export const HpeItemType = createEnum('HPE_ITEM_TYPE', {
  Item: 'ITEM',
  Component: 'Component',
  SalesTax: 'SALES_TAX',
  Divider: 'DIVIDER',
});
export type HpeItemType = EnumSet<typeof HpeItemType>;

export type WithHpeFormula<T> = T & {
  formula?: Formula;
  oldFormula?: Formula;
  topIds?: ID[];
  isFormulaNeedPropagation: boolean;
};

export type HpeDetailsInit = HpeDetailsInitDto & { priceBookId?: ID };

export type HpSourceJobListItem = JobListItemDto & { name: string };
export type HpeCreateDetails = {
  housePlanId: ID;
  initType: HpeInitType;
  sourceHousePlanId?: ID;
  sourceJob?: HpSourceJobListItem;
};
export type HpeDetails = WithOptimisticLock<{
  priceBookId?: ID;
  housePlanId: ID;
  version: EntityVersion;
  code: string;
  name: string;
  cost: number;
  total: number;
  viewMode: HpeViewMode;
  hpTops: HpeTop[];
  hpCostCenters: HpeCostCenter[];
  hpComponents: HpeComponent[];
  hpItems: HpeItem[];
  tops: HpeTopRefItem[];
  uoms: HpeUomRefItem[];
  masterZoneHouseEstimatePlansFolderId: ID;
  memo?: string;
}>;
export type HpeDetailsProcess = HpeDetails & { initialCostCenterId?: ID; initialCostCenter?: HpeDetailsGetCostCenter };

export type HpeUomRefItem = HpeUomRefItemDto;
export type HpeTopRefItem = HpeTopRefItemDto;
export type HpeCostCenter = WithDirtyType<{
  costCenterId: ID;
  code: number;
  name: string;
  version: EntityVersion;
  memo?: string;
  cost?: number;
  total?: number;
  hpCostCenterId?: ID;
  preloaded: boolean;
  count: number;
  salesTax?: HpeSalesTax;
  topIds?: ID[];
}>;
export type HpeTop = WithEntityState<WithDirtyType<{
  topId: ID;
  count: number;
  isFromGto: boolean;
  code: string;
  value?: Quantity<any>;
  gtoUom: GtoUom;
}>>;
export type HpeComponent = WithEntityState<WithDirtyType<WithHpeFormula<WithCreatedDate<{
  hpComponentId: ID;
  uomId: ID;
  componentId: ID;
  code: string;
  name: string;
  memo: string;
  cost: number;
  isActive: boolean;
  extension: number;
  quantity: number;
  items: HpeComponentItem[];
  cuttingList?: HpeCuttingListItem[];
}>>>>;
export type HpeItem = WithEntityState<WithDirtyType<WithHpeFormula<WithCreatedDate<{
  hpItemId: ID;
  itemId: ID;
  hpComponentId?: ID;
  costCenterId: ID;
  uomId: ID;
  code: string;
  name: string;
  cost: number;
  extension: number;
  quantity: number;
  isActive: boolean;
  cuttingList?: HpeCuttingListItem[];
  memo?: string;
  // topIds: ID[];
  type: HpeItemType;
  isSalesTax: boolean;
}>>>>;
export type HpeElement = HpeItem | HpeComponent;
export type HpeComponentItem = HpeComponentItemDto;
export type HpeSalesTax = HpeGetCostCenterResponseSalesTaxDto;

// tslint:disable-next-line:variable-name
export enum HpeElementType { Component = 'Component', Item = 'Item' }

export type HpeCuttingListItem = WithEntityState<WithUniqueId<WithCreatedDate<WithDirtyType<{
  sortOrder: number;
  sourceId: ID;
  sourceType: HpeElementType;
  description: string;
  quantity: number;
  pieces?: number;
  dimension1Value?: number;
  dimension2Value?: number;
  dimension3Value?: number;
  hasPieces: boolean;
  hasDimension1Value: boolean;
  hasDimension2Value: boolean;
  hasDimension3Value: boolean;
}>>>>;

export type HpeDetailsGetCostCenterRequest = {
  housePlanId: ID;
  costCenterId: ID;
  priceBookId?: ID;
  costCenters: HpeCostCenter[];
  components: HpeComponent[];
  items: HpeItem[];
  tops: HpeTop[];
};

export type HpeDetailsGetCostCenter = {
  version: EntityVersion;
  code: string;
  name: string;
  cost: number;
  total: number;
  items: HpeItem[];
  memo?: string;
  salesTax?: HpeSalesTax;
};

export type HpeDetailsGetComponents = {
  components: HpeComponent[];
  items: HpeItem[];
  affectedCostCenters: HpeCostCenter[];
};

export type HpeDetailsSave = {
  priceBookId?: ID;
  housePlanId: ID;
  hpCostCenters: HpeCostCenter[];
  hpComponents: HpeComponent[];
  hpItems: HpeItem[];
  hpTops: HpeTop[];
  memo?: string;
};

export type HpeTopViewUsageRecord = {
  costCenterId: ID;
  recordId: ID;
  costCenterCode: number;
  recordCode: string;
};

export type HpeTopViewUsage = {
  hpCode: string;
  hpName: string;
  topName: string;
  records: HpeTopViewUsageRecord[];
};

export interface HpeMappingContext {
  getUom(id: ID): Maybe<HpeUomRefItem>;

  getTop(id: ID): Maybe<HpeTopRefItem>;
}

export type HpeCcOrderDetails = HpeCcOrderDetailsDto;
export type HpeCcOrderDetailsItem = HpeCcOrderDetailsItemDto;

export const getHpeUomRefItemId = (uom: HpeUomRefItem) => uom.uomId;
export const getHpeTopRefItemId = (uom: HpeTopRefItem) => uom.topId;
export const getHpeTopId = (top: HpeTop) => top.topId;
export const getHpeCostCenterId = (cc: HpeCostCenter) => cc.costCenterId;
export const getHpeComponentId = (component: HpeComponent) => component.hpComponentId;
export const getHpeItemId = (item: HpeItem) => item.hpItemId;
export const getHpeComponentItemId = (item: HpeComponentItem) => `${item.costCenterId}:${item.itemId}`;
export const getHpeCuttingListItemId = (item: HpeCuttingListItem) => item.uniqueId;

export const getHpeTopQuantityType = (top: HpeTop) => getUomQuantityType(top.gtoUom);

export const isHpeSalesTaxCode = (code: string) => code === 'SALESTAX';

const isHpeCuttingListFieldRequired = (entity: HpeCuttingListItem, field: string) => {
  switch (field) {
    case 'description':
      return !entity.hasPieces
        && !entity.hasDimension1Value && !entity.hasDimension2Value && !entity.hasDimension3Value;
    case 'pieces':
      return entity.hasPieces;
    case 'dimension1Value':
      return entity.hasDimension1Value;
    case 'dimension2Value':
      return entity.hasDimension2Value;
    case 'dimension3Value':
      return entity.hasDimension3Value;
    default:
      return true;
  }
};

const HPE_TOP_ENTITY_CONFIG: EntityConfig<HpeTop> = {
  value: {
    required: true,
    editable: (top: HpeTop) => !isHpeContractPriceTop(top),
    type: getHpeTopQuantityType,
  },
};

const HPE_CUTTING_LIST_ITEM_ENTITY_CONFIG: EntityConfig<HpeCuttingListItem> = {
  description: {
    required: isHpeCuttingListFieldRequired,
    editable: true,
  },
  pieces: {
    required: isHpeCuttingListFieldRequired,
    editable: true,
    type: NumberTypes.QuantityIntegral,
  },
  quantity: {
    required: isHpeCuttingListFieldRequired,
    editable: true,
    type: NumberTypes.Quantity,
  },
  dimension1Value: {
    required: isHpeCuttingListFieldRequired,
    editable: true,
    type: NumberTypes.Dimension,
  },
  dimension2Value: {
    required: isHpeCuttingListFieldRequired,
    editable: true,
    type: NumberTypes.Dimension,
  },
  dimension3Value: {
    required: isHpeCuttingListFieldRequired,
    editable: true,
    type: NumberTypes.Dimension,
  },
};

const HPE_COMPONENT_ENTITY_CONFIG: EntityConfig<HpeComponent> = {
  quantity: {
    required: true,
    editable: ({ formula, cuttingList }: HpeComponent) => Boolean(isFormulaEmpty(formula) && isEmpty(cuttingList)),
    type: NumberTypes.SignedQuantity,
  },
  formula: {
    validator: fieldValidatorFrom(formulaValidator),
  },
  cuttingList: {
    validator: fieldValidatorFrom(nestedArrayValidator),
  },
};

const HPE_ITEM_ENTITY_CONFIG: EntityConfig<HpeItem> = {
  quantity: {
    required: true,
    editable: (item: HpeItem) =>
      isFormulaEmpty(item.formula) && isEmpty(item.cuttingList) && item.type === HpeItemType.Item,
    type: NumberTypes.SignedQuantity,
  },
  formula: {
    validator: fieldValidatorFrom(formulaValidator),
  },
  cuttingList: {
    validator: fieldValidatorFrom(nestedArrayValidator),
  },
};

export const validateHpeTopField = fieldValidatorByEntityConfig(HPE_TOP_ENTITY_CONFIG);
export const hpeTopFieldState = fieldStateByEntityConfig(HPE_TOP_ENTITY_CONFIG);

export const hpeComponentFieldState = fieldStateByEntityConfig(HPE_COMPONENT_ENTITY_CONFIG);
export const validateHpeComponentField = fieldValidatorByEntityConfig(HPE_COMPONENT_ENTITY_CONFIG);

export const validateHpeCuttingListItemField = fieldValidatorByEntityConfig(HPE_CUTTING_LIST_ITEM_ENTITY_CONFIG);
export const validateHpeCuttingListItem = entityValidatorByEntityConfig(HPE_CUTTING_LIST_ITEM_ENTITY_CONFIG);
export const hpeCuttingListItemFieldState = fieldStateByEntityConfig(HPE_CUTTING_LIST_ITEM_ENTITY_CONFIG);
export const complementHpeCuttingListItem = entityStateMerger(errorsEntityState(validateHpeCuttingListItem));

export const hpeItemFieldState = fieldStateByEntityConfig(HPE_ITEM_ENTITY_CONFIG);

export const validateHpeItemField = fieldValidatorByEntityConfig(HPE_ITEM_ENTITY_CONFIG);

export const hpeFormulaNeedPropagationSetter = (entity: HpeItem | HpeComponent, name: string, _: any) => {
  if (name === 'formula') {
    return setEntityField(entity, 'isFormulaNeedPropagation', true);
  } else {
    return entity;
  }
};

/**
 * We have to reset quantity to '1' if cuttingList or formula becomes empty
 */
export const hpeQuantityResetSetter = (entity: HpeItem | HpeComponent, name: string, _: any) => {
  switch (name) {
    case 'formula': {
      const { formula } = entity;
      if (isNil(formula) || formula.valid && isEmpty(formula.expression)) {
        return setEntityField(entity, 'quantity', 1);
      } else {
        return entity;
      }
    }
    case 'cuttingList': {
      const { cuttingList } = entity;
      if (isEmpty(cuttingList)) {
        return setEntityField(entity, 'quantity', 1);
      } else {
        return entity;
      }
    }
    default:
      return entity;
  }
};

export const newHpeCuttingListItem = (uomId: ID,
                                      sortOrder: number,
                                      sourceType: HpeElementType,
                                      sourceId: ID,
                                      mappingContext: HpeMappingContext) => {
  const uom = mappingContext.getUom(uomId);

  return {
    uniqueId: generateHpeCuttingListItemId(),
    sortOrder,
    sourceType,
    sourceId,
    description: '',
    quantity: 1,
    hasPieces: uom && uom.usePieces || false,
    hasDimension1Value: uom && !isEmpty(uom.dimension1) || false,
    hasDimension2Value: uom && !isEmpty(uom.dimension2) || false,
    hasDimension3Value: uom && !isEmpty(uom.dimension3) || false,
    createdDate: currentDate(),
  };
};

export const hpeViewModeToFormMode = (viewMode: HpeViewMode) => ({
  [HpeViewMode.Create]: FormMode.CreateMode,
  [HpeViewMode.Edit]: FormMode.EditMode,
  [HpeViewMode.View]: FormMode.ViewMode,
})[viewMode];
export const hpeDetailsInitFromCreate = (details: HpeCreateDetails) => {
  const { sourceJob } = details;
  return {
    ...omit(details, 'sourceJob'),
    sourceJobId: sourceJob && sourceJob.id,
  };
};
export const hpeDetailsInitToDto = (init: HpeDetailsInit) => cast<HpeDetailsInitDto>(omit(init, ['priceBookId']));

export function hpeDetailsFromInitDto(dto: HpeDetailsDto): HpeDetails {
  const mappingContext = createInitHpeMappingContext(dto);

  return {
    ...cast<HpeDetails>(omit(dto, 'hpComponents', 'hpCostCenters')),
    hpCostCenters: map(dto.hpCostCenters, hpeCostCenterFromDto),
    hpComponents: map(dto.hpComponents, (component) => hpeComponentFromDto(component, mappingContext)),
    hpItems: <HpeItem[]>flatMap(
      dto.hpCostCenters,
      (cc) => (cc.items || [])
        .filter((item) => !isHpeItemFromComponent(item))
        .map((item) => hpeItemFromCostCenterItemDto(cc.costCenterId, item, mappingContext))),
    hpTops: map(dto.hpTops, (top) => hpeTopFromTopDto(top, mappingContext)),
    memo: dto.memo,
  };
}

export function hpeTopFromTopDto(top: HpeTopDto, mappingContext: HpeMappingContext): HpeTop {
  const refTop = mappingContext.getTop(top.topId)!; // tslint:disable-line:no-non-null-assertion
  return {
    ...top,
    code: refTop.code,
    value: isNotNil(top.value) ? createUomQuantity(top.value, refTop.gtoUom) : void 0,
    gtoUom: refTop.gtoUom,
  };
}

export function hpeDetailsFromRecalculateDto(dto: HpeDetailsDto, mappingContext: HpeMappingContext): HpeDetails {
  return {
    ...cast<HpeDetails>(omit(dto, 'hpComponents', 'hpCostCenters')),
    hpCostCenters: map(dto.hpCostCenters.filter(isHpeTypicalCostCenter), hpeCostCenterFromDto),
    hpComponents: map(dto.hpComponents, (component) => hpeComponentFromDto(component, mappingContext)),
    hpItems: <HpeItem[]>flatMap(
      dto.hpCostCenters,
      (cc) => (cc.items || [])
        .map((item) => hpeItemFromCostCenterItemDto(cc.costCenterId, item, mappingContext))),
    hpTops: map(dto.hpTops, (top) => hpeTopFromTopDto(top, mappingContext)),
    memo: dto.memo,
  };
}

export function hpeDetailsGetComponentsFromDto(dto: HpeGetComponentsResponseDto,
                                               mappingContext: HpeMappingContext): HpeDetailsGetComponents {
  return {
    affectedCostCenters: dto.affectedCostCenters.map(hpeCostCenterFromDto),
    components: dto.components.map((component) => hpeComponentFromDto(component, mappingContext)).map(setCreatedDate),
    items: <HpeItem[]>flatMap(
      dto.components,
      (component) => component.items.map((item) => hpeItemFromComponentItemDto(item, mappingContext))),
  };
}

export function hpeComponentFromDto(dto: HpeComponentDto, mappingContext: HpeMappingContext): HpeComponent {
  return {
    ...<HpeComponent>omit(dto, 'cuttingList'),
    oldFormula: dto.formula,
    items: dto.items || [],
    isFormulaNeedPropagation: false,
    topIds: dto.topIds ? normalizeTakeOffParams(dto.topIds) : void 0,
    cuttingList: (dto.cuttingList || [])
      .map((item) => hpeCuttingListItemFromDto(
        dto.uomId,
        dto.hpComponentId,
        HpeElementType.Component,
        item,
        mappingContext)),
  };
}

export function hpeCuttingListItemFromDto(uomId: Maybe<ID>,
                                          sourceId: ID,
                                          sourceType: HpeElementType,
                                          dto: HpeCuttingListItemDto,
                                          mappingContext: HpeMappingContext): HpeCuttingListItem {
  const uom = uomId && mappingContext.getUom(uomId);

  return {
    ...dto,
    uniqueId: generateHpeCuttingListItemId(),
    sourceType,
    sourceId, // tslint:disable-line:no-non-null-assertion
    hasPieces: uom && uom.usePieces || false,
    hasDimension1Value: uom && !isEmpty(uom.dimension1) || false,
    hasDimension2Value: uom && !isEmpty(uom.dimension2) || false,
    hasDimension3Value: uom && !isEmpty(uom.dimension3) || false,
  };
}

export function hpeItemFromComponentItemDto(dto: HpeComponentItemDto, mappingContext: HpeMappingContext): HpeItem {
  return {
    ...dto,
    hpItemId: generateHpeComponentItemId(dto.hpComponentId, dto.costCenterId, dto.itemId),
    type: HpeItemType.Component,
    isSalesTax: false,
    isFormulaNeedPropagation: false,
  };
}

export function hpeCostCenterFromDto(dto: HpeCostCenterRefDto): HpeCostCenter {
  return {
    ...cast<HpeCostCenter>(omit(dto, 'items')),
    preloaded: isHpeComponentCostCenter(dto),
    count: dto.items ? dto.items.length : 0,
  };
}

export function hpeCostCenterToDto(model: HpeCostCenter): HpeCostCenterRefDto {
  return {
    ...cast<HpeCostCenterRefDto>(omit(model, 'dirtyType')),
    deleted: model.dirtyType === DirtyType.Removed,
  };
}

export function hpeCostCenterFromFinder(found: CostCenterFinderListItem): HpeCostCenter {
  return {
    costCenterId: found.id,
    code: found.code,
    name: found.name,
    version: 1,
    cost: 0,
    count: 0,
    preloaded: false,
  };
}

export function hpeCostCenterItemsFromFinder(costCenterId: ID,
                                             ids: ID[],
                                             items: ComponentItemFinderListItem[],
                                             mappingContext: HpeMappingContext): HpeItem[] {
  return items.map((item, i) => hpeCostCenterItemFromFinder(costCenterId, ids[i], item, mappingContext));
}

export function hpeCostCenterItemFromFinder(costCenterId: ID,
                                            hpItemId: ID,
                                            found: ComponentItemFinderListItem,
                                            mappingContext: HpeMappingContext): HpeItem {
  return {
    costCenterId,
    isSalesTax: found.isSalesTax,
    type: HpeItemType.Item,
    isActive: found.active,
    quantity: 1,
    cost: found.cost,
    extension: found.cost,
    itemId: found.id,
    code: found.code,
    name: found.name,
    uomId: found.uomId,
    formula: found.formula,
    topIds: found.topIds,
    memo: found.memo,
    isFormulaNeedPropagation: false,
    hpItemId,
    createdDate: currentDate(),
  };
}

export function hpeCostCenterToDtoForGetCostCenter(cc: HpeCostCenter): HpeGetCostCenterRequestCostCenterDto {
  return <HpeGetCostCenterRequestCostCenterDto>pick(
    cc,
    ['hpCostCenterId', 'costCenterId', 'version', 'dirtyType', 'memo']);
}

export function hpeComponentToDtoForGetCostCenter(component: HpeComponent): HpeGetCostCenterRequestComponentDto {
  return {
    ...<HpeGetCostCenterRequestComponentDto>pick(
      component,
      ['hpComponentId', 'componentId', 'formula', 'quantity', 'isActive', 'dirtyType']),
    cuttingList: hpeCuttingListToDto(component.cuttingList || []),
  };
}

export function hpeItemToDtoForGetCostCenter(item: HpeItem): HpeGetCostCenterRequestItemDto {
  return {
    ...<HpeGetCostCenterRequestItemDto>pick(
      item,
      ['itemId', 'costCenterId', 'formula', 'quantity', 'memo', 'isActive', 'dirtyType']),
    hpItemId: isHpeFreeItem(item) ? item.hpItemId : void 0,
    cuttingList: hpeCuttingListToDto(item.cuttingList || []),
  };

}

export function hpeTopToDtoForGetCostCenter(top: HpeTop): HpeGetCostCenterRequestTopDto {
  return <HpeGetCostCenterRequestTopDto>pick(top, ['topId', 'value', 'count']);
}

export function hpeDetailsGetCostCenterFromDto(costCenterId: ID,
                                               costCenter: HpeGetCostCenterResponseDto,
                                               mappingContext: HpeMappingContext): HpeDetailsGetCostCenter {
  return {
    ...<HpeDetailsGetCostCenter>pick(costCenter, ['version', 'code', 'name', 'cost', 'total']),
    items: costCenter.items.map((item) => hpeItemFromCostCenterItemDto(costCenterId, item, mappingContext)),
    salesTax: costCenter.salesTax,
    memo: costCenter.memo,
  };
}

export function hpeItemFromCostCenterItemDto(costCenterId: ID,
                                             dto: HpeGetCostCenterResponseItemDto | HpeCostCenterItemRefDto,
                                             mappingContext: HpeMappingContext): HpeItem {
  return {
    ...<HpeItem>dto,
    oldFormula: dto.formula,
    type: dto.hpComponentId ? HpeItemType.Component
      : (isHpeSalesTaxCode(dto.code) ? HpeItemType.SalesTax : HpeItemType.Item),
    costCenterId,
    topIds: dto.topIds ? normalizeTakeOffParams(dto.topIds) : void 0,
    hpItemId: isHpeSalesTaxCode(dto.code) ? `sales-tax:${costCenterId}`
      // tslint:disable-next-line:no-non-null-assertion
      : (dto.hpItemId || generateHpeComponentItemId(dto.hpComponentId!, costCenterId, dto.itemId)),
    isFormulaNeedPropagation: false,
    cuttingList: (dto.cuttingList || [])
      .map((item) => hpeCuttingListItemFromDto(
        dto.uomId,
        dto.hpItemId,
        HpeElementType.Item,
        item,
        mappingContext)),
  };
}

export const hpeFormulaVariableFromTopDto = ({ topId, code, isActive }: HpeTopRefItemDto) =>
  ({ id: topId, name: code, active: isActive });

export const hpeFormulaVariableFromTop = hpeFormulaVariableFromTopDto;

export function mergeLoadedCostCenter(current: HpeCostCenter, loaded: HpeDetailsGetCostCenter): HpeCostCenter {
  return {
    ...current,
    cost: loaded.cost,
    version: loaded.version,
    salesTax: loaded.salesTax,
    memo: loaded.memo,
    preloaded: true,
    total: loaded.total,
  };
}

export function isHpeTypicalCostCenter(costCenter: HpeCostCenter | HpeCostCenterRefDto): boolean {
  return !isHpeComponentCostCenter(costCenter);
}

export function isHpeComponentCostCenter(costCenter: HpeCostCenter | HpeCostCenterRefDto): boolean {
  return costCenter.code === 0;
}

export function generateHpeComponentItemId(hpComponentId: ID, costCenterId: ID, itemId: ID): ID {
  return `item:${hpComponentId}:${costCenterId}:${itemId}`;
}

export function generateHpeCuttingListItemId(): ID {
  return uniqueId('cutting-list-item:');
}

export function isHpeItemFromComponent(item: { hpComponentId?: ID }): boolean {
  return !isNil(item.hpComponentId);
}

export function isHpeFreeItem(item: HpeItem): boolean {
  return item.type === HpeItemType.Item;
}

export function createHpeTop(topRefItem: HpeTopRefItem): HpeTop {
  return {
    topId: topRefItem.topId,
    code: topRefItem.code,
    isFromGto: false,
    value: createUomQuantity(0, topRefItem.gtoUom),
    gtoUom: topRefItem.gtoUom,
    count: 1,
  };
}

export function getHpeComponentCostCenterIds(component: HpeComponent): ID[] {
  return component.items.map((item) => item.costCenterId);
}

export function getHpeComponentTopIds(component: HpeComponent): ID[] {
  return component.topIds || [];
}

export function getHpeItemTopIds(item: HpeItem): ID[] {
  return item.topIds || [];
}

export const hpeCostCenterToSaveDto = (cc: HpeCostCenter) => <HpeDetailsSaveRequestCostCenterDto>pick(
  cc,
  ['hpCostCenterId', 'costCenterId', 'version', 'dirtyType', 'memo']);

export const hpeCuttingListToDto = (cuttingList: HpeCuttingListItem[]) =>
  <HpeCuttingListItemDto[]>cuttingList.map((item) => pick(
    item,
    compact([
      'sortOrder',
      'description',
      item.hasPieces && 'pieces',
      item.hasDimension1Value && 'dimension1Value',
      item.hasDimension2Value && 'dimension2Value',
      item.hasDimension3Value && 'dimension3Value'])));

export const hpeComponentToSaveDto = (component: HpeComponent) => ({
  ...<HpeDetailsSaveRequestComponentDto>pick(
    component,
    ['hpComponentId', 'componentId', 'quantity', 'formula', 'dirtyType', 'memo']),
  cuttingList: hpeCuttingListToDto(component.cuttingList || []),
});

export const hpeItemToSaveDto = (item: HpeItem) => ({
  ...<HpeDetailsSaveRequestItemDto>pick(
    item,
    ['itemId', 'costCenterId', 'quantity', 'formula', 'memo', 'isActive', 'dirtyType', 'memo']),
  hpItemId: isHpeFreeItem(item) ? item.hpItemId : void 0,
  cuttingList: hpeCuttingListToDto(item.cuttingList || []),
});

export const hpeTopToSaveDto = (top: HpeTop) =>
  <HpeDetailsSaveRequestTopDto>pick(top, ['topId', 'count', 'value', 'isFromGto']);

export function hpeDetailsSaveToDto(model: HpeDetailsSave, isSave: boolean): HpeDetailsSaveRequestDto {
  const componentCC = model.hpCostCenters.filter(isHpeComponentCostCenter);
  return {
    priceBookId: model.priceBookId,
    housePlanId: model.housePlanId,
    hpCostCenters: model.hpCostCenters.filter(isHpeTypicalCostCenter).map(hpeCostCenterToSaveDto),
    hpComponents: model.hpComponents.map(hpeComponentToSaveDto),
    hpItems: model.hpItems.map(hpeItemToSaveDto),
    hpTops: model.hpTops.map(hpeTopToSaveDto),
    memo: componentCC.length ? componentCC[0].memo : model.memo,
    isSave,
  };
}

export const hpeUomRefItemFromDto = (dto: UomDetailsDto): HpeUomRefItem => ({
  uomId: dto.id,
  code: dto.code,
  usePieces: dto.usePieces,
  dimension1: dto.dimension1 || dto.dimension1User,
  dimension2: dto.dimension2 || dto.dimension2User,
  dimension3: dto.dimension3 || dto.dimension3User,
});

export const hpeTopRefItemFromDto = (dto: TakeOffParamDto): HpeTopRefItem => ({
  topId: dto.takeOffParamId,
  code: dto.code,
  gtoUom: dto.gtoUom!, // tslint:disable-line:no-non-null-assertion
  isActive: dto.isActive,
  isCommon: true,
});

export const isHpeComponentFieldAffectsPrice = (name: string) => name !== 'memo';
export const isHpeItemFieldAffectsPrice = (name: string) => name !== 'memo';
export const isHpeCuttingListItemFieldAffectsPrice = (name: string) => name !== 'description';

export const isHpeContractPriceTop = (top: HpeTop) => top.code === TOP_CODE_CONTRACT_PRICE;

export const isHpeRecordForComponent = (record: HpeTopViewUsageRecord) => record.costCenterCode === 0;

export const hpeDetailsGetCostCenterRequestToDto = ({
                                                      priceBookId,
                                                      costCenters,
                                                      components,
                                                      items,
                                                      tops,
                                                    }: HpeDetailsGetCostCenterRequest) => ({
  priceBookId,
  hpCostCenters: costCenters.filter(isHpeTypicalCostCenter).map(hpeCostCenterToDtoForGetCostCenter),
  hpComponents: components.map(hpeComponentToDtoForGetCostCenter),
  hpItems: items.map(hpeItemToDtoForGetCostCenter),
  hpTops: tops.map(hpeTopToDtoForGetCostCenter),
});

export const hpeDetailsToInitCostCenterRequestDto = (details: HpeDetails, costCenterId: ID) => ({
  housePlanId: details.housePlanId,
  costCenterId,
  costCenters: [],
  components: [],
  items: [],
  tops: details.hpTops,
});

export const isHpeCcNotPreloaded = (cc: HpeCostCenter) => !cc.preloaded;

export const createInitHpeMappingContext = (dto: HpeDetailsDto | HpeDetails): HpeMappingContext => {
  const uoms = keyBy(dto.uoms, 'uomId');
  const tops = keyBy(dto.tops, 'topId');

  return {
    getTop: (id) => tops[id],
    getUom: (id) => uoms[id],
  };
};

export function isHpeComponent(element: HpeElement): element is HpeComponent {
  return isNil(element['hpItemId']);
}

export const isHpeElementHasInvalidFormula = ({ formula }: HpeElement) => formula && !formula.valid;
